import React from 'react'
import useGallery from './hooks/useGallery'
import GalleryUploadModal from './components/GalleryUploadModal'
import GalleryLightboxModal from './components/GalleryLightboxModal'

export default function GalleryScreen() {
  const { state, actions } = useGallery()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-20">
      {/* ── Header ── */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Cloud &amp; Campus Engagement
          </p>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
              Photo Gallery
            </h1>
            <span className="rounded-xl bg-surface-container-high px-3 py-1 text-xs font-bold text-on-surface-variant">
              {state.count} pictures
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => actions.setIsUploadModalOpen(true)}
            className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-extrabold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">add_photo_alternate</span>
            <span>Add New Picture</span>
          </button>
        </div>
      </section>

      {/* ── Guidelines Info Banner ── */}
      <div className="rounded-3xl border border-primary/20 bg-primary/[0.04] p-4 sm:p-5 flex items-start gap-3 text-xs text-on-surface-variant leading-relaxed">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
          <span className="material-symbols-outlined text-base">info</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <h3 className="font-bold text-on-surface">Picture Gallery Guidelines</h3>
          <p>
            These pictures are visible to students and parents when they log-in on the MyInstitute mobile app &amp; web portal.
          </p>
        </div>
      </div>

      {/* ── Photo Gallery Grid ── */}
      {state.loading ? (
        <div className="flex flex-col items-center justify-center py-20 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary mb-2">
            progress_activity
          </span>
          <p className="text-xs text-on-surface-variant">Loading gallery pictures...</p>
        </div>
      ) : state.photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-surface-container-high text-outline-variant mb-4">
            <span className="material-symbols-outlined text-3xl">photo_library</span>
          </div>
          <h3 className="font-display text-base font-bold text-on-surface">No Pictures in Gallery</h3>
          <p className="text-xs text-on-surface-variant mt-1 max-w-sm">
            Upload campus photos, events, academic ceremonies, and student achievements.
          </p>
          <button
            type="button"
            onClick={() => actions.setIsUploadModalOpen(true)}
            className="mt-4 flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">add_photo_alternate</span>
            <span>Upload First Picture</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {state.photos.map((photo) => (
            <div
              key={photo.id}
              className="group relative overflow-hidden rounded-3xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs hover:shadow-md transition-all flex flex-col"
            >
              {/* Image Container */}
              <div className="relative h-48 w-full overflow-hidden bg-surface-container-high">
                <img
                  src={photo.imageUrl}
                  alt={photo.caption}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[2px]">
                  <button
                    type="button"
                    onClick={() => actions.setSelectedPhoto(photo)}
                    title="View Enlarged Picture"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-on-surface hover:bg-white shadow-md transition cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-lg">zoom_in</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.handleDelete(photo.id)}
                    title="Delete Picture"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-600/90 text-white hover:bg-rose-600 shadow-md transition cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </div>

              {/* Caption & Info */}
              <div className="p-4 flex flex-col justify-between flex-1 gap-2">
                <p className="text-xs font-bold text-on-surface line-clamp-2 leading-snug">
                  {photo.caption}
                </p>
                <div className="flex items-center justify-between text-[11px] text-on-surface-variant pt-2 border-t border-outline-variant/15">
                  <span>{photo.uploadedAt}</span>
                  <span className="font-semibold text-primary">{photo.fileSize || 'JPG'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Upload Modal ── */}
      <GalleryUploadModal
        isOpen={state.isUploadModalOpen}
        onClose={() => actions.setIsUploadModalOpen(false)}
        onSave={actions.handleUpload}
        isSaving={state.isSaving}
      />

      {/* ── Lightbox Modal ── */}
      <GalleryLightboxModal
        photo={state.selectedPhoto}
        onClose={() => actions.setSelectedPhoto(null)}
      />
    </div>
  )
}
